import { defineStore } from "pinia";
import { ref } from "vue";
import { useToast } from "vue-toastification";
import devicesService from "@/services/device";
import sitesService from "@/services/site";

export const useDevicesStore = defineStore("devices", () => {
  const toast = useToast();
  const loading = ref(false);
  const deviceDataObject = ref(null);
  const remoteControlObject = ref(null);

  function setDeviceDataObject(payload) {
    deviceDataObject.value = payload;
  }

  function setRemoteControlDataObject(payload) {
    remoteControlObject.value = payload;
  }

  async function addDevice(payload) {
    loading.value = true;
    try {
      const res = await devicesService.addDevice(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function editDevice(payload) {
    loading.value = true;
    try {
      const res = await devicesService.editDevice(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchAllDevices(companyId) {
    loading.value = true;
    try {
      const res = await devicesService.getAllDevices(companyId);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchAppsVersions(payload) {
    loading.value = true;
    try {
      const res = await devicesService.getAppsVersions(payload);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function openDoor(payload) {
    loading.value = true;
    try {
      const res = await devicesService.openDoor(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function restartDevice(payload) {
    loading.value = true;
    try {
      const res = await devicesService.restartDevice(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function massRestartDevice(payload) {
    loading.value = true;
    try {
      const res = await devicesService.massRestartDevice(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function restartApp(payload) {
    loading.value = true;
    try {
      const res = await devicesService.restartApp(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function installApp(payload) {
    loading.value = true;
    try {
      const res = await devicesService.installApp(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function massInstallApp(payload) {
    loading.value = true;
    try {
      const res = await devicesService.massInstallApp(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function uninstallApp(payload) {
    loading.value = true;
    try {
      const res = await devicesService.uninstallApp(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function clearAppData(payload) {
    loading.value = true;
    try {
      const res = await devicesService.clearAppData(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function clearAppCache(payload) {
    loading.value = true;
    try {
      const res = await devicesService.clearAppCache(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function forceStopApp(payload) {
    loading.value = true;
    try {
      const res = await devicesService.forceStopApp(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function launchApp(payload) {
    loading.value = true;
    try {
      const res = await devicesService.launchApp(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function deleteRequestJob(payload) {
    loading.value = true;
    try {
      const res = await devicesService.deleteRequestJob(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function refreshFaceLibrary(payload) {
    loading.value = true;
    try {
      const res = await devicesService.refreshFaceLibrary(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function moveDevice(payload) {
    loading.value = true;
    try {
      const res = await devicesService.moveDevice(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function massMoveDevices(payload) {
    loading.value = true;
    try {
      const res = await devicesService.massMoveDevices(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function deregisterDevice(payload) {
    loading.value = true;
    try {
      const res = await devicesService.deregisterDevice(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function massDeregisterDevices(payload) {
    loading.value = true;
    try {
      const res = await devicesService.massDeregisterDevices(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function applyConfig(payload) {
    loading.value = true;
    try {
      const res = await devicesService.applyConfig(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function massApplyConfigs(payload) {
    loading.value = true;
    try {
      const res = await devicesService.massApplyConfigs(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function applySiteAndAreaConfig(payload) {
    loading.value = true;
    try {
      const res = await sitesService.applySiteAndAreaConfig(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function updateDeviceSettings(payload) {
    loading.value = true;
    try {
      const res = await devicesService.updateDeviceSettings(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchScreenshot(payload) {
    loading.value = true;
    try {
      const res = await devicesService.getScreenshot(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchMassScreenshot(payload) {
    loading.value = true;
    try {
      const res = await devicesService.getMassScreenshot(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchDeviceInfo(payload) {
    loading.value = true;
    try {
      const res = await devicesService.getDeviceInfo(payload);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchInstalledApps(mac_address) {
    loading.value = true;
    try {
      const res = await devicesService.getInstalledApps(mac_address);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchInstalledAppsGrid(payload) {
    loading.value = true;
    try {
      const res = await devicesService.getInstalledAppsGrid(payload);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchDeviceRemoteControlAction(payload) {
    loading.value = true;
    try {
      const res = await devicesService.remoteControlNotifyToStart(payload);
      return res;
    } catch (err) {
      toast.error("connection not found");
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchRoomInfo(mac_address) {
    loading.value = true;
    try {
      const res = await devicesService.getRoomInfo(mac_address);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchDeviceRefreshGetScreen(payload) {
    loading.value = true;
    try {
      const res = await devicesService.refreshGetScreen(payload);
      toast.success(res.message);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchDevicesLogs(payload) {
    loading.value = true;
    try {
      const res = await devicesService.getDevicesLogs(payload);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchDeviceLogs(payload) {
    loading.value = true;
    try {
      const res = await devicesService.getDeviceLogs(payload);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchDeviceStatusAndTypesLogs() {
    loading.value = true;
    try {
      const res = await devicesService.getDeviceStatusAndTypesLogs();
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchDevicesBySearch(payload) {
    loading.value = true;
    try {
      const res = await devicesService.getDevicesBySearch(payload);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function getScreenAgentsList(payload) {
    loading.value = true;
    try {
      const res = await devicesService.getScreenAgentsList(payload);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function getScreenAgentConnection(payload) {
    loading.value = true;
    try {
      const res = await devicesService.getScreenAgentConnection(payload);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function getScreenRemoveAgentConnection(payload) {
    loading.value = true;
    try {
      const res = await devicesService.getScreenRemoveAgentConnection(payload);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  return {
    loading,
    deviceDataObject,
    remoteControlObject,
    setDeviceDataObject,
    setRemoteControlDataObject,
    addDevice,
    editDevice,
    fetchAllDevices,
    fetchAppsVersions,
    openDoor,
    restartDevice,
    massRestartDevice,
    restartApp,
    installApp,
    massInstallApp,
    uninstallApp,
    clearAppData,
    clearAppCache,
    forceStopApp,
    launchApp,
    deleteRequestJob,
    refreshFaceLibrary,
    moveDevice,
    massMoveDevices,
    deregisterDevice,
    massDeregisterDevices,
    applyConfig,
    massApplyConfigs,
    applySiteAndAreaConfig,
    updateDeviceSettings,
    fetchScreenshot,
    fetchMassScreenshot,
    fetchDeviceInfo,
    fetchInstalledApps,
    fetchInstalledAppsGrid,
    fetchDeviceRemoteControlAction,
    fetchRoomInfo,
    fetchDeviceRefreshGetScreen,
    fetchDevicesLogs,
    fetchDeviceLogs,
    fetchDeviceStatusAndTypesLogs,
    fetchDevicesBySearch,
    getScreenAgentsList,
    getScreenAgentConnection,
    getScreenRemoveAgentConnection,
  };
});
